import { fade, makeStyles } from "@material-ui/core";
import { MEDIA_CLIP_HEIGHT } from "./mediaHeight";

/**
 * The avatar and the gap beside it, in one place: the indent that aligns the post with the name is
 * their sum, so the three numbers cannot be allowed to drift apart.
 */
const AVATAR = 40;
const GAP = 16;
const AVATAR_XS = 36;
const GAP_XS = 12;

const useSocialCardStyles = makeStyles(theme => ({
    root: {
        marginBottom: 10,
        [theme.breakpoints.down("xs")]: {
            marginBottom: 0,
            borderBottom: "1px solid #CCC",
            borderRadius: 0,
            marginLeft: theme.spacing(-1),
            marginRight: theme.spacing(-1),
        },
    },
    header: {
        alignItems: "flex-start",
        display: "flex",
        padding: theme.spacing(2,2,0),
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1.5,1,0),
        },
    },
    avatar: {
        display: "flex",
        flex: "0 0 auto",
        marginRight: GAP,
        [theme.breakpoints.down("sm")]: {
            marginRight: GAP_XS,
        },
    },
    avatarImage: {
        height: AVATAR,
        width: AVATAR,
        boxShadow: "0 3px 6px 0 rgba(0, 0, 0, 0.16)",
        color: theme.palette.type === "light" ? theme.palette.background.paper : theme.palette.background.default,
        backgroundColor: theme.palette.type === "light" ? "#757575" : theme.palette.secondary.dark,
        fontSize: "0.875rem",
        [theme.breakpoints.down("sm")]: {
            height: AVATAR_XS,
            width: AVATAR_XS,
        },
    },
    content: {
        flex: "1 1 auto",
        // Without this a long unbroken word - a URL in a display name - pushes the time off the card
        // instead of wrapping, because a flex item's min-width is auto.
        minWidth: 0,
    },
    /**
     * Name and time on one line. The name is the only thing at full strength up here; everything else
     * in the header is a qualifier and is set quieter than the post's own text.
     */
    nameLine: {
        alignItems: "baseline",
        display: "flex",
        gap: theme.spacing(1),
    },
    /**
     * Wraps rather than truncating. An institutional contributor's name is the whole of its identity -
     * "European Conservatives and Reformists Group" has no first and last name behind it to fall back
     * on - so an ellipsis there costs more than a second line does.
     */
    name: {
        overflowWrap: "anywhere",
    },
    /**
     * The group and country, as one quiet line: "EPP · Finland". The full party name is in the link's
     * title - it runs to sixty characters ("Group of the European People's Party (Christian
     * Democrats)") and set at the old weight it competed with the person's own name.
     */
    affiliation: {
        color: theme.palette.text.hint,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        "& a": {
            color: "inherit",
            textDecoration: "none",
        },
        "& a:hover": {
            textDecoration: "underline",
        },
    },
    /** Time and network mark, pushed to the end of the name line. Never wraps, never shrinks. */
    source: {
        alignItems: "center",
        color: theme.palette.text.hint,
        display: "flex",
        flex: "0 0 auto",
        gap: theme.spacing(0.75),
        marginLeft: "auto",
        whiteSpace: "nowrap",
    },
    /**
     * The network, as a bare mark rather than the grey disc it used to sit in. The disc was the third
     * heavy object in a row that already held a 56px avatar and the timestamp, and it said what the
     * "View on X" button below says in words.
     */
    sourceIcon: {
        display: "flex",
        opacity: 0.45,
        "& svg": {
            height: theme.spacing(2),
            width: theme.spacing(2),
        },
    },
    /**
     * Everything below the name hangs off one edge, aligned with the name rather than with the card.
     * The card used to have two left edges - the header indented past the avatar, the post text back
     * at the card's own padding - and no single column to read down.
     */
    stem: {
        paddingLeft: theme.spacing(2) + AVATAR + GAP,
        paddingRight: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            paddingLeft: theme.spacing(1) + AVATAR_XS + GAP_XS,
            paddingRight: theme.spacing(1),
        },
    },
    counter: {
        margin: theme.spacing(0, 0, 1),
    },
    media: {
        borderRadius: theme.shape.borderRadius,
        maxWidth: "100%",
        width: "100%",
    },
    image: {
        height: "auto !important",
    },
    /**
     * Clips an over-tall image. Only applied once onLoad has measured one as tall - see mediaHeight.
     * Position is relative so mediaFade can sit against the bottom edge.
     */
    mediaClip: {
        position: "relative",
        maxHeight: MEDIA_CLIP_HEIGHT,
        overflow: "hidden",
        borderRadius: theme.shape.borderRadius,
    },
    /**
     * Fades the clipped edge into the card so it reads as "there is more" rather than as a picture
     * that happens to end abruptly. `fade(..., 0)` rather than "transparent": transparent is
     * transparent *black* in a gradient, which greys the middle of the fade.
     *
     * The colour is the theme's paper, not white - this app ships a dark theme too.
     */
    mediaFade: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: theme.spacing(8),
        pointerEvents: "none",
        backgroundImage: `linear-gradient(to bottom, ${fade(theme.palette.background.paper, 0)}, ${theme.palette.background.paper})`,
    },
    /**
     * Deliberately quiet. It is an aside about how the picture is displayed, not an action competing
     * with Translate, View on X and Share below it - so secondary text colour and a smaller size
     * rather than the primary blue those use.
     */
    mediaToggle: {
        display: "block",
        margin: theme.spacing(0.5, "auto", 0),
        textTransform: "none",
        // text.hint, not text.secondary: this palette defines text.secondary as a blue (#2e73ea light,
        // #8ab4f8 dark), so it is the loud option here, not the quiet one. hint is a real grey in both
        // themes - #555555 and #bdc1c6.
        color: theme.palette.text.hint,
        fontSize: "0.8125rem",
        fontWeight: theme.typography.fontWeightRegular,
    },
    audio: {
    },
    video: {
        height: "auto !important",
    },
    toolbar: {
        backgroundColor: theme.palette.type === "light" ? theme.palette.primary.main : theme.palette.primary.main,
        color: theme.palette.type === "light" ? theme.palette.primary.main : theme.palette.primary.main,
        display: "flex",
        height: 87,
        justifyContent: "center",
        overflowY: "hidden",
        transition: `${theme.transitions.create("height")}, ${theme.transitions.create("min-height")}`,
        zIndex: 3,
        "& *":{
            "&:focus-visible": {
                /*outlineOffset: "1px",
                outline:"2px solid black",*/
                borderRadius: "0px !important",
                outline: "3px dotted #d9d9d9",
                outlineOffset: "-3px",
            },
            "&:-moz-focusring": {
                /*outlineOffset: "1px",
                outline:"2px solid black",*/
                borderRadius: "0px !important",
                outline: "3px dotted #d9d9d9",
                outlineOffset: "-3px",
            },
        },
    },
    toolbarCollapsed: {
        height: 0,
        minHeight: 0,
        padding: 0,
    },
    toolbarContent: {
        display: "flex",
        justifyContent: "space-between",
        overflow: "hidden",
        flexGrow: 1,
    },
    toolbarItem: {
        display: "flex",
        flexDirection: "column",
        justfyContent: "center",
        alignItems: "center",
        width: "100%",
        textAlign: "center",
        padding: theme.spacing(2, 0),
        border: 0,
        cursor: "pointer",
        textDecoration: "none",
        backgroundColor: theme.palette.type === "light" ? theme.palette.primary.main : theme.palette.primary.main,
        [theme.breakpoints.up("md")]: {
            "&:focus-visible": {
                borderRadius: "0px !important",
                outline: "3px dotted #d9d9d9 !important",
                outlineOffset: "-3px",
            },
            "&:-moz-focusring": {
                borderRadius: "0px !important",
                outline: "3px dotted #d9d9d9 !important",
                outlineOffset: "-3px",
            },
        },
        "&:hover": {
            backgroundColor:  theme.palette.type === "light" ? theme.palette.primary.dark : theme.palette.secondary.dark,
            "& svg": {
                color:  theme.palette.type === "light" ? theme.palette.primary.dark : theme.palette.secondary.dark,
            },
        },
        "& *": {
            textDecoration: "none",
        },
        "& p": {
            fontSize: 12,
            color: theme.palette.type === "light" ? theme.palette.background.paper : theme.palette.background.paper,
            [theme.breakpoints.down("sm")]: {
                fontSize: 10,
            },
        },
        "&>div": {
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: theme.palette.type === "light" ? theme.palette.background.paper : theme.palette.background.paper,
            marginBottom: 5,
            "&>svg": {
                color: theme.palette.type === "light" ? theme.palette.primary.main : theme.palette.primary.main,
                width: 18,
                height: 32
            },
        },
    },
    cardActions: {
        // A hairline, not theme.palette.divider - this palette defines divider as the EP yellow, which
        // is a statement rather than a rule.
        borderTop: `1px solid ${fade(theme.palette.text.primary, 0.08)}`,
        padding: theme.spacing(0.5, 0),
        // Cancels the buttons' own 8px so the first label sits on the stem's edge, under the text.
        marginLeft: theme.spacing(-1),
        "& .MuiButton-label": {
            textTransform: "none",
            fontSize: 14,
            fontWeight: 400,
            color: theme.palette.type === "light" ? theme.palette.secondary.dark : theme.palette.text.primary,
            [theme.breakpoints.down("sm")]: {
                fontSize: 12,
            },
        },
        "& a":{
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            textDecoration: "none !important",
            "&:hover":{
                backgroundColor: "transparent !important",
                "& .MuiButton-label": {
                    color: theme.palette.type === "light" ? theme.palette.primary.main + " !important" : theme.palette.primary.main + " !important",
                },
            },
        },
        "& button":{
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            "&:hover":{
                backgroundColor: "transparent !important",
                "& .MuiButton-label": {
                    color: theme.palette.type === "light" ? theme.palette.primary.main + " !important" : theme.palette.primary.main + " !important",
                },
            },
        },
    },
    /**
     * What this post is in relation to another one - reposted, quoted, in reply. Its own line above
     * the text, where it qualifies the post; it used to be appended to "Tweeted" in the header, which
     * read as a fact about the author and said the network's name a third time.
     *
     * Nothing is rendered at all for an original post: in a feed of posts, "Tweeted" is not news.
     */
    postType: {
        alignItems: "center",
        color: theme.palette.text.hint,
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        "& a": {
            color: "inherit",
            textDecoration: "none",
        },
        "& a:hover": {
            textDecoration: "underline",
        },
    },
    postTypeIcon: {
        fontSize: "1rem",
        opacity: 0.7,
    },
    /**
     * The quoted post under a quote's own words, drawn the way X draws it: a bordered box, the whole
     * of it one link to the post. Inside, the quoted account and a line saying where the link goes,
     * since the box has no text of the post to show.
     */
    quotedPost: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        color: theme.palette.text.primary,
        textDecoration: "none",
        "&:hover, &:focus-visible": {
            backgroundColor: theme.palette.action.hover,
            textDecoration: "none",
        },
    },
    quotedPostHeader: {
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.5),
        fontSize: theme.typography.subtitle2.fontSize,
    },
    quotedPostAvatar: {
        height: 20,
        width: 20,
        fontSize: "0.75rem",
    },
    quotedPostAuthor: {
        fontWeight: 700,
    },
    quotedPostLabel: {
        color: theme.palette.text.hint,
    },
    /** The quoted words, clipped at four lines: the box opens the post for the rest. */
    quotedPostText: {
        display: "-webkit-box",
        overflow: "hidden",
        whiteSpace: "pre-line",
        wordBreak: "break-word",
        "-webkit-box-orient": "vertical",
        "-webkit-line-clamp": 4,
    },
    quotedPostLink: {
        color: theme.palette.primary.main,
        fontSize: theme.typography.subtitle2.fontSize,
        "$quotedPost:hover &": {
            textDecoration: "underline",
        },
    },
    /**
     * Horizontal padding lives on the stem now, so the post text keeps the name's left edge. Only the
     * gap above and below the text is set here.
     */
    contentContainer: {
        padding: theme.spacing(0, 0, 1),
        "&:last-child": {
            paddingBottom: theme.spacing(1),
        },
    },
    cardMedia: {
        "& .MuiToolbar-gutters": {
            padding: 0,
        },
    },
    mediaContainer: {
        position: "relative",
        "& .MuiCardContent-root": {
            padding: 0,
        },
        '& a':{
            /*backgroundColor: "blue",*/

        },
        '& svg':{
            position: "absolute",
            right: 0,
            left: 0,
            bottom: 0,
            top: 0,
            margin: "auto",
            width: 90,
            height: 90,
            cursor: "pointer",
            color: theme.palette.type === "light" ? "white" : "white",
            "-webkit-filter": "drop-shadow( 3px 3px 2px rgba(0, 0, 0, .7))",
            filter: "drop-shadow( 3px 3px 2px rgba(0, 0, 0, .7))",
            [theme.breakpoints.down("sm")]: {
                width: 50,
                height: 50,
            },
        }
    },
    cardText: {
        "& > a": {
            color: theme.palette.type === "light" ? theme.palette.primary.main : theme.palette.primary.main,
            "&:focus-visible": {
                outlineOffset: "0px",
            },
            "&:-moz-focusring": {
                outlineOffset: "0px",
            },
        }
    },
    /**
     * A post's own line breaks, which HTML otherwise folds into spaces (ops issues/083). pre-line,
     * not pre-wrap: a run of spaces still folds, so an indented line or a double space after a full
     * stop does not open a gap. Not on cardText, which the author's name shares.
     */
    postText: {
        whiteSpace: "pre-line",
    },
    cardTextTranslation: {
        whiteSpace: "pre-line",
        padding: theme.spacing(0, 0, 0, 2),
        margin: theme.spacing(1, 0, 2),
        borderLeft: theme.palette.type === "light" ? "4px solid #cecece " : "4px solid" + theme.palette.secondary.dark,
    },
    translateButton: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    active: {
        "&.MuiButton-root":{
            "& .MuiButton-label":{
                color: theme.palette.type === "light" ? theme.palette.primary.dark + " !important"  : theme.palette.primary.dark + " !important",
                "&:hover":{
                    color: theme.palette.type === "light" ? theme.palette.primary.dark + " !important" : theme.palette.primary.dark + " !important",
                },
            },
        },
    },
    snackbar: {
        [theme.breakpoints.down("sm")]: {
            bottom: "56px",
        },
    },
}));

export { useSocialCardStyles };
